import createClient, { type Middleware } from 'openapi-fetch';

import { depuisReponse, erreurReseau, ErreurApi } from './erreurs';
import type { paths } from './genere';
import { lireJetonAcces, obtenirJetonFrais, viderJetons } from './jetons';

/**
 * Client HTTP de l'administration de plateforme.
 *
 * MEME OPENAPI QUE L'APP UTILISATEUR, CLIENT DISTINCT. Le contrat est commun — c'est la
 * meme API — mais aucun fichier n'est partage entre les deux fronts (§0.2, §3). Un module
 * commun deviendrait un chemin par lequel du code d'administration pourrait se retrouver
 * dans le bundle utilisateur, ou l'inverse.
 *
 * `paths` est genere depuis l'OpenAPI `/v1` : les routes `/v1/admin/*` sont donc typees
 * comme le reste, et un endpoint d'administration renomme cote back casse ce build.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

const authentification: Middleware = {
  async onRequest({ request }) {
    const jeton = lireJetonAcces();

    if (jeton) {
      request.headers.set('Authorization', `Bearer ${jeton}`);
    }

    return request;
  },
};

/**
 * Rejoue UNE FOIS une requete refusee en 401, apres rafraichissement.
 *
 * Le drapeau `x-astrolabe-rejeu` empeche la boucle infinie. Un 401 qui persiste apres
 * rafraichissement vide la session : sur cet outil, une session dont on ne sait plus si
 * elle est valide doit se fermer, pas se prolonger.
 *
 * ATTENTION : ce middleware ne touche PAS aux 403. Un 403 signifie « ce compte n'est pas
 * administrateur plateforme » ; le rejouer avec un jeton frais donnerait le meme refus, et
 * masquerait le message que l'utilisateur doit voir.
 */
const rejeuApresExpiration: Middleware = {
  async onResponse({ request, response }) {
    if (response.status !== 401 || request.headers.has('x-astrolabe-rejeu')) {
      if (response.status === 401) {
        viderJetons();
      }

      return response;
    }

    const jetonFrais = await obtenirJetonFrais();

    if (!jetonFrais) {
      viderJetons();
      return response;
    }

    const rejeu = new Request(request.clone(), { headers: new Headers(request.headers) });
    rejeu.headers.set('Authorization', `Bearer ${jetonFrais}`);
    rejeu.headers.set('x-astrolabe-rejeu', '1');

    return fetch(rejeu);
  },
};

export const clientApi = createClient<paths>({ baseUrl: BASE_URL });

clientApi.use(authentification);
clientApi.use(rejeuApresExpiration);

/**
 * Normalise la reponse d'`openapi-fetch` en valeur ou exception.
 *
 * TanStack Query attend une promesse qui rejette en cas d'echec ; `openapi-fetch` renvoie
 * un couple `{ data, error }`. Ce deballage est le seul endroit ou la conversion se fait,
 * ce qui garantit que toute erreur remontant a l'interface est une `ErreurApi`.
 */
export async function deballer<T>(
  appel: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let resultat: { data?: T; error?: unknown; response: Response };

  try {
    resultat = await appel;
  } catch (cause) {
    throw erreurReseau(cause);
  }

  if (resultat.error !== undefined && resultat.error !== null) {
    throw depuisReponse(resultat.response.status, resultat.error);
  }

  if (!resultat.response.ok) {
    throw depuisReponse(resultat.response.status, null);
  }

  // 204 : succes sans corps. Les actes d'administration repondent souvent ainsi.
  return (resultat.data ?? (undefined as T)) as T;
}

export { ErreurApi };
